import { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';
import { ENV_CONFIG } from 'config/environment';
import { sortRolesByPriority } from 'config/permissions';
import { useAuth0Config } from 'hooks/useAuth0Config';
import { useMixpanelAdminContext } from 'hooks/useMixpanelAdminContext';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { adminsActiveRole } from 'store/admins/slice';
import { getActiveRole } from 'selectors/admins';
import {
  setRoleType,
  setUserName,
  getRoleType,
  clearRolesandResources,
  setRoleTypeOptions,
} from 'services/storage';
import { datadogLogs } from '@datadog/browser-logs';
import { mixpanelClient } from 'helpers/mixpanel';

interface AuthInitializerProps {
  children: React.ReactNode;
}

const AuthInitializer = ({ children }: AuthInitializerProps) => {
  const dispatch = useAppDispatch();
  const { isLoading, isAuthenticated, user, loginWithRedirect } = useAuth0();
  const { handleAccessToken, handleLogout } = useAuth0Config();
  const activeRole = useAppSelector(getActiveRole);
  const [auth0Response, setAuth0Response] = useState<any>();
  const [isInitialized, setIsInitialized] = useState(false);
  useMixpanelAdminContext();

  const rolesClaim = `${ENV_CONFIG().SITE_URL}/roles`;
  // Computed once per decoded token so the init effect doesn't re-sort on every render
  const userRoles = useMemo(
    () =>
      auth0Response && !auth0Response.error
        ? sortRolesByPriority(auth0Response[rolesClaim] ?? [])
        : [],
    [auth0Response, rolesClaim]
  );

  useEffect(() => {
    if (userRoles.length) {
      setRoleTypeOptions(userRoles);
    }
  }, [userRoles]);

  useEffect(() => {
    if (!isLoading && isAuthenticated && !auth0Response) {
      handleAccessToken()
        .then(token => {
          if (token) {
            setAuth0Response(jwtDecode(token));
          }
        })
        .catch(error => {
          datadogLogs.logger.warn('[AuthInitializer] Failed to get token:', {}, error);
          setAuth0Response({ error: 'token_fetch_failed' });
        });
    }
  }, [isLoading, isAuthenticated, auth0Response]);

  useEffect(() => {
    if (isAuthenticated && user?.sub) {
      mixpanelClient.identifyWithMergeEvent(user.sub);
    }
  }, [isAuthenticated, user?.sub]);

  useEffect(() => {
    // Skip if already initialized to prevent re-running this logic
    if (isInitialized) {
      return;
    }

    if (!isLoading) {
      if (!isAuthenticated) {
        datadogLogs.logger.debug('[AuthInitializer] logging in');
        loginWithRedirect().then(() =>
          datadogLogs.logger.debug('[AuthInitializer] log-in via redirect completed')
        );
        return;
      }

      if (auth0Response && !auth0Response.error) {
        if (!userRoles.length) {
          datadogLogs.logger.warn('[AuthInitializer] No recognized admin role, logging out', {
            tokenRoles: auth0Response[rolesClaim] ?? [],
          });
          handleLogout();
          return;
        }

        if (!activeRole) {
          const storedRole = getRoleType();
          const initialRole =
            storedRole && userRoles.includes(storedRole) ? storedRole : userRoles[0];

          setRoleType(initialRole);
          dispatch(adminsActiveRole(initialRole));
          // Don't set isInitialized yet - wait for activeRole to be updated in Redux
          return;
        }

        // Only proceed if we have an activeRole (either already set or just dispatched)
        if (!activeRole) {
          return;
        }

        if (user) {
          setUserName(user?.name || user?.email || '');
        } else {
          datadogLogs.logger.warn('[AuthInitializer] User data missing, logging out');
          handleLogout();
          return;
        }

        setIsInitialized(true);
      } else if (auth0Response?.error) {
        datadogLogs.logger.warn(
          '[AuthInitializer] Auth0 response has error:',
          { error: auth0Response.error },
          new Error('Unexpected Auth0 Error, logging out')
        );
        if (process.env.REACT_APP_REFRESH_RBAC_LOCAL_STORAGE === 'true') {
          datadogLogs.logger.debug(`Clearing user's storage and logging out`);
          clearRolesandResources();
        }
        handleLogout();
        return;
      }
    }
  }, [
    auth0Response,
    userRoles,
    isLoading,
    isAuthenticated,
    activeRole,
    user,
    dispatch,
    handleLogout,
    isInitialized,
    loginWithRedirect,
  ]);

  return <>{children}</>;
};

export default AuthInitializer;
